import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { formatDate } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { StatusBadge, PriorityBadge } from '../components/StatusBadge';
import { Search, FileText, ClipboardList, Box, DoorOpen, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SearchResultsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  const [branchFilter, setBranchFilter] = useState(searchParams.get('branch') || 'all');

  useEffect(() => {
    loadBranches();
  }, []);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      performSearch(q);
    }
  }, [searchParams, typeFilter, branchFilter]);

  const loadBranches = async () => {
    try {
      const { data } = await api.get('/branches');
      setBranches(data);
    } catch (err) {
      console.error('Failed to load branches:', err);
    }
  };

  const performSearch = async (q) => {
    if (!q || q.length < 2) return;
    
    setLoading(true);
    try {
      const params = { q, limit: 50 };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (branchFilter !== 'all') params.branch_id = branchFilter;
      
      const { data } = await api.get('/search', { params });
      setResults(data);
    } catch (err) {
      toast.error(t('toast.error'));
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.length >= 2) {
      setSearchParams({ q: query, type: typeFilter, branch: branchFilter });
    }
  };

  const totalResults = results?.total_count || 0;
  const isRTL = i18n.language === 'ar';

  return (
    <div data-testid="search-results-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="shrink-0"
          data-testid="back-btn"
        >
          <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
        </Button>
        <div className="flex-1">
          <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">{t('search.results')}</h1>
          {results && (
            <p className="text-muted-foreground text-sm mt-1">
              {t('search.resultsFor')} "{searchParams.get('q')}" — {totalResults} {t('search.totalResults').toLowerCase()}
            </p>
          )}
        </div>
      </div>

      {/* Search Bar and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('search.placeholder')}
              className="ps-9"
              data-testid="search-input"
            />
          </div>
          <Button type="submit" className="bg-brand hover:bg-brand-hover text-brand-foreground">
            {t('common.search')}
          </Button>
        </form>
        
        <div className="flex gap-2">
          <Select value={typeFilter} onValueChange={(v) => {
            setTypeFilter(v);
            setSearchParams({ q: query, type: v, branch: branchFilter });
          }}>
            <SelectTrigger className="w-[160px]" data-testid="type-filter">
              <SelectValue placeholder={t('search.filterByType')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('search.allTypes')}</SelectItem>
              <SelectItem value="wo">{t('search.workOrders')}</SelectItem>
              <SelectItem value="wr">{t('search.workRequests')}</SelectItem>
              <SelectItem value="asset">{t('search.assets')}</SelectItem>
              <SelectItem value="room">{t('search.rooms')}</SelectItem>
            </SelectContent>
          </Select>
          
          {branches.length > 1 && (
            <Select value={branchFilter} onValueChange={(v) => {
              setBranchFilter(v);
              setSearchParams({ q: query, type: typeFilter, branch: v });
            }}>
              <SelectTrigger className="w-[180px]" data-testid="branch-filter">
                <SelectValue placeholder={t('search.filterByBranch')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')} {t('common.branches')}</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : !results ? (
        <div className="text-center py-16 text-muted-foreground">
          <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{t('search.placeholder')}</p>
        </div>
      ) : totalResults === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">{t('search.noResults')}</p>
        </div>
      ) : (
        <div className="grid gap-6">
          {/* Work Orders */}
          {(typeFilter === 'all' || typeFilter === 'wo') && results?.work_orders?.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-brand" />
                  {t('search.workOrders')} ({results.work_orders.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">ID</th>
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.status')}</th>
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.branch')}</th>
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.room')}</th>
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('workOrder.assignedTo')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.work_orders.map(wo => (
                        <tr
                          key={wo.id}
                          className="border-b border-border hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/work-orders/${wo.id}`)}
                        >
                          <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{wo.id.slice(0, 8)}</td>
                          <td className="px-5 py-3"><StatusBadge status={wo.status} /></td>
                          <td className="px-5 py-3 text-foreground">{wo.branch_name}</td>
                          <td className="px-5 py-3 text-foreground" data-testid={`search-wo-room-${wo.id}`}>
                            {wo.room_name || <span className="text-muted-foreground italic">{t('common.noRoom')}</span>}
                          </td>
                          <td className="px-5 py-3 text-muted-foreground">{wo.assigned_to_name || t('workOrder.unassigned')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Work Requests */}
          {(typeFilter === 'all' || typeFilter === 'wr') && results?.work_requests?.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
                  <FileText className="w-5 h-5 text-brand" />
                  {t('search.workRequests')} ({results.work_requests.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">ID</th>
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.name')}</th>
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.status')}</th>
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.branch')}</th>
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.room')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.work_requests.map(wr => (
                        <tr
                          key={wr.id}
                          className="border-b border-border hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/work-requests/${wr.id}`)}
                        >
                          <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{wr.id.slice(0, 8)}</td>
                          <td className="px-5 py-3 text-foreground font-medium truncate max-w-[200px]">{wr.title}</td>
                          <td className="px-5 py-3"><StatusBadge status={wr.status} /></td>
                          <td className="px-5 py-3 text-muted-foreground">{wr.branch_name}</td>
                          <td className="px-5 py-3 text-foreground" data-testid={`search-wr-room-${wr.id}`}>
                            {wr.room_name || <span className="text-muted-foreground italic">{t('common.noRoom')}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Assets */}
          {(typeFilter === 'all' || typeFilter === 'asset') && results?.assets?.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
                  <Box className="w-5 h-5 text-brand" />
                  {t('search.assets')} ({results.assets.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('assets.serial')}</th>
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.name')}</th>
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('assets.category')}</th>
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.branch')}</th>
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.room')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.assets.map(asset => (
                        <tr
                          key={asset.id}
                          className="border-b border-border hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/rooms?tab=assets&highlight=${asset.id}`)}
                        >
                          <td className="px-5 py-3 font-mono text-xs text-brand">{asset.serial || '—'}</td>
                          <td className="px-5 py-3 text-foreground font-medium">{asset.name}</td>
                          <td className="px-5 py-3 text-muted-foreground">{asset.category || '—'}</td>
                          <td className="px-5 py-3 text-muted-foreground">{asset.branch_name}</td>
                          <td className="px-5 py-3 text-muted-foreground">{asset.room_name || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Rooms */}
          {(typeFilter === 'all' || typeFilter === 'room') && results?.rooms?.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
                  <DoorOpen className="w-5 h-5 text-brand" />
                  {t('search.rooms')} ({results.rooms.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('rooms.code')}</th>
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.name')}</th>
                        <th className="text-start px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.branch')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.rooms.map(room => (
                        <tr
                          key={room.id}
                          className="border-b border-border hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/rooms?tab=rooms&highlight=${room.id}`)}
                        >
                          <td className="px-5 py-3 font-mono text-xs text-brand">{room.code || '—'}</td>
                          <td className="px-5 py-3 text-foreground font-medium">{room.name}</td>
                          <td className="px-5 py-3 text-muted-foreground">{room.branch_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
